import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, ScrollText, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { SettingsTabs } from "@/components/SettingsTabs";
import { SETTINGS } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const ALL = "__all__";

function kindColor(kind = "") {
  if (kind.startsWith("member") || kind.startsWith("workspace")) return "text-blue-300 border-blue-400/30 bg-blue-400/10";
  if (kind.startsWith("signal")) return "text-emerald-300 border-emerald-400/30 bg-emerald-400/10";
  if (kind.includes("restricted")) return "text-red-300 border-red-400/30 bg-red-400/10";
  if (kind.startsWith("alert")) return "text-amber-300 border-amber-400/30 bg-amber-400/10";
  return "text-muted-foreground border-border/60 bg-muted/40";
}

function fmt(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function SettingsAudit() {
  const { active } = useWorkspace();
  const canManage = active && (active.role === "owner" || active.role === "admin");

  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [actor, setActor] = useState(ALL);
  const [kind, setKind] = useState(ALL);

  const load = useCallback(async () => {
    if (!active) return;
    setData(null);
    try {
      const params = { workspace_id: active.id, limit: 300 };
      if (actor !== ALL) params.actor = actor;
      if (kind !== ALL) params.kind = kind;
      const { data: res } = await api.get("/audit", { params });
      setData(res);
    } catch (err) {
      if (err.response?.status === 403) setDenied(true);
      else toast.error("Failed to load audit log");
    }
  }, [active, actor, kind]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = { workspace_id: active.id };
      if (actor !== ALL) params.actor = actor;
      if (kind !== ALL) params.kind = kind;
      const res = await api.get("/audit/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit_log.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const events = data?.events || [];

  return (
    <div data-testid={SETTINGS.auditRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-5xl px-6 py-10">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Settings</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Immutable, append-only record of every change in <span className="text-foreground">{active?.name}</span>.
        </p>
        <SettingsTabs />

        {!canManage || denied ? (
          <div data-testid={SETTINGS.denied} className="mt-10 rounded-xl border border-border/60 bg-card/40 p-8 text-center text-sm text-muted-foreground">
            <ShieldAlert className="mx-auto mb-3 h-6 w-6 text-amber-400" />
            The audit log requires an admin or owner role.
          </div>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Select value={actor} onValueChange={setActor}>
                <SelectTrigger data-testid={SETTINGS.auditActorFilter} className="h-9 w-[200px] text-xs"><SelectValue placeholder="All actors" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All actors</SelectItem>
                  {(data?.actors || []).map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid={SETTINGS.auditKindFilter} className="h-9 w-[200px] text-xs"><SelectValue placeholder="All events" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All events</SelectItem>
                  {(data?.kinds || []).map((k) => (<SelectItem key={k} value={k}>{k}</SelectItem>))}
                </SelectContent>
              </Select>
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={!events.length}
                data-testid={SETTINGS.auditExport}
                className="h-9 rounded-full border-border/80 text-xs uppercase tracking-[0.16em]"
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>

            <div className="mt-5 overflow-hidden rounded-xl border border-border/60">
              {data === null ? (
                <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
              ) : events.length === 0 ? (
                <div data-testid={SETTINGS.auditEmpty} className="py-16 text-center text-sm text-muted-foreground">
                  <ScrollText className="mx-auto mb-3 h-6 w-6 opacity-50" />
                  No audit events match these filters yet.
                </div>
              ) : (
                events.map((e) => (
                  <div
                    key={e.id}
                    data-testid={SETTINGS.auditRow}
                    className="flex items-start justify-between gap-4 border-b border-border/40 px-4 py-3 last:border-0 hover:bg-muted/30"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${kindColor(e.kind)}`}>
                          {e.kind}
                        </span>
                        <span className="text-xs text-foreground">{e.actor_email || "system"}</span>
                      </div>
                      {e.payload && Object.keys(e.payload).length > 0 && (
                        <pre className="mt-1 truncate text-[11px] text-muted-foreground">{JSON.stringify(e.payload)}</pre>
                      )}
                    </div>
                    <div className="shrink-0 text-right text-[11px] text-muted-foreground">{fmt(e.created_at)}</div>
                  </div>
                ))
              )}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Append-only ledger — events cannot be edited or deleted. {events.length} event{events.length === 1 ? "" : "s"} shown.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
